import { useState, type MouseEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { CANCEL_EXECUTION_PERMISSION, can, useIdentity } from '@/entities/session';
import { DatasetKindIcon } from '@/features/catalog-browser';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog';
import { EmptyState } from '@/shared/ui/EmptyState';
import { HelpLabel } from '@/shared/ui/InfoTip';
import { PageHeader } from '@/shared/ui/PageHeader';
import { SegmentedFilter } from '@/shared/ui/SegmentedFilter';
import { SkeletonRows } from '@/shared/ui/Skeleton';
import { ageText } from '@/shared/model/age';
import { compactNumber } from '@/shared/model/number';
import { personName } from '@/shared/model/person';

import type { Execution, ExecutionFindings, Task } from '@/shared/api/generated/rules';
import type { Score } from '@/shared/api/generated/scoring';
import {
  SOURCE_FILTERS,
  matchesSource,
  sourceOf,
  type SourceFilter,
} from '@/features/scheduling';
import { scoreBand, useComputeScores } from '@/features/scoring';
import {
  useCancelExecution,
  useExecutionFindings,
  useExecutionTasks,
  useExecutions,
  useRuleNamesByVersion,
} from '../api/rules';

import { shortRunId } from '../model/presentation';

/* The run ledger as the redesign draws it: three counts, a source switch, and one row per run that
 * opens in place to show what the run covered, its quality score and each task.
 *
 * **"Checked and found nothing" and "not started" are different facts, everywhere on the row.** A
 * queued run has a hatched progress track and "Not measured yet" findings — never an empty bar and a
 * zero, which is exactly what a clean run looks like. The state chip's hollow dot carries the same
 * rule for a run nothing has happened to yet.
 *
 * **Compute score is in the row**, because `may_publish_a_score` is the verdict this ledger exists
 * to lead to. The POST is idempotent per run — a repeat returns the stored scores unchanged — so the
 * button is safe to press twice and the figure it shows is always the stored one.
 */

/* The design's state chip, per execution state: the hue it wears and whether its dot is hollow. A
 * total map, so a seventh state is a compile error rather than a row in the everything-is-fine tone. */
const STATE_CHIP = {
  pending: { tone: 'unknown', hollow: true },
  running: { tone: 'info', hollow: false },
  succeeded: { tone: 'ok', hollow: false },
  partial: { tone: 'warn', hollow: false },
  failed: { tone: 'error', hollow: false },
  cancelled: { tone: 'unknown', hollow: false },
} as const satisfies Record<
  Execution['state'],
  { readonly tone: string; readonly hollow: boolean }
>;

const SCORE_TONE = { ok: 'ok', warn: 'warn', danger: 'error' } as const;

/** How many target tables the opened row names before it says how many more. */
const TARGETS_SHOWN = 6;

function StateChip({ state }: { readonly state: Execution['state'] }): ReactElement {
  const { t } = useTranslation();
  const chip = STATE_CHIP[state];
  return (
    <span className="lq-cn__state lq-ex__state" data-tone={chip.tone}>
      <span
        aria-hidden="true"
        className={chip.hollow ? 'lq-cn__dot lq-cn__dot--hollow' : 'lq-cn__dot'}
      />
      {t(`executions.state.${state}`)}
    </span>
  );
}

/* Three segments — succeeded, failed, cancelled — on a hatched track, so the unfinished part of a run
 * reads as not-yet-measured rather than as empty. The counts are in the line beneath and in the
 * accessible name, never only in a width. */
function ProgressCell({ execution }: { readonly execution: Execution }): ReactElement {
  const { t } = useTranslation();
  const { total, succeeded, failed, cancelled } = execution.progress;
  const finished = succeeded + failed + cancelled;
  const share = (part: number): string =>
    total > 0 ? `${String(Math.min(100, (part / total) * 100))}%` : '0%';

  return (
    <div className="lq-ex__progress">
      <div
        className="lq-ex__bar"
        role="progressbar"
        aria-valuenow={total > 0 ? Math.round((finished / total) * 100) : undefined}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={
          total === 0
            ? t('executions.progress.notWritten')
            : t('executions.progress.finished', { finished, total })
        }
      >
        <span className="lq-ex__seg lq-ex__seg--ok" style={{ width: share(succeeded) }} />
        <span className="lq-ex__seg lq-ex__seg--fail" style={{ width: share(failed) }} />
        <span
          className="lq-ex__seg lq-ex__seg--cancel"
          style={{ width: share(cancelled) }}
        />
      </div>
      <span className="lq-ex__progressText">
        {total === 0
          ? t('executions.progress.notWritten')
          : failed > 0
            ? t('executions.progress.finishedFailed', { finished, total, failed })
            : t('executions.progress.finished', { finished, total })}
      </span>
    </div>
  );
}

/* What the run found. `null` violations is "not measured yet" — a queued run, or one where no task
 * succeeded — and never a zero, which is what a clean run reads as. */
function FindingsCell({
  execution,
  findings,
}: {
  readonly execution: Execution;
  readonly findings: ExecutionFindings | undefined;
}): ReactElement {
  const { t } = useTranslation();
  const violations = findings?.violations ?? null;
  const rows = findings?.rows_evaluated ?? null;

  return (
    <div>
      <div
        className="lq-ex__findings"
        data-measured={violations === null ? 'false' : 'true'}
      >
        {violations === null
          ? t('executions.findings.notMeasured')
          : t('executions.findings.violations', {
              count: violations,
              shown: compactNumber(violations),
            })}
      </div>
      <div className="lq-ex__sub">
        {rows === null || findings === undefined
          ? execution.state === 'pending'
            ? t('executions.findings.queuedNote')
            : t('executions.findings.noTaskSucceeded')
          : findings.partial
            ? t('executions.findings.inRowsPartial', {
                rows: compactNumber(rows),
                measured: findings.measured_tasks,
                total: findings.total_tasks,
              })
            : t('executions.findings.inRows', { rows: compactNumber(rows) })}
      </div>
    </div>
  );
}

/** The mean of the scores a run produced, or `null` when none of them had a total. */
function meanOf(scores: readonly Score[]): number | null {
  const values = scores.flatMap((score) => (score.value === null ? [] : [score.value]));
  if (values.length === 0) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function modelLine(
  t: ReturnType<typeof useTranslation>['t'],
  scores: readonly Score[],
): string {
  const version = scores[0]?.score_model_version ?? 0;
  return scores.length === 1
    ? t('executions.score.modelVersion', { version })
    : t('executions.score.manyDatasets', { count: scores.length, version });
}

/* The design's score cell: the computed figure with a mini bar, the Compute score action where the
 * run may publish one, or a hatched bar saying why it cannot. Never a zero for "not computed". */
function ScoreCell({
  execution,
  scores,
  busy,
  failed,
  onCompute,
}: {
  readonly execution: Execution;
  readonly scores: readonly Score[] | undefined;
  readonly busy: boolean;
  readonly failed: boolean;
  readonly onCompute: (event: MouseEvent) => void;
}): ReactElement {
  const { t } = useTranslation();

  if (scores !== undefined) {
    const mean = meanOf(scores);
    if (mean === null) {
      return (
        <span className="lq-ex__sub">
          {scores.length === 0
            ? t('executions.score.noneScored')
            : t('executions.score.notComputable')}
        </span>
      );
    }
    const tone = SCORE_TONE[scoreBand(mean)];
    return (
      <div className="lq-ex__scoreBox">
        <div className="lq-ex__scoreLine">
          <span className="lq-ex__scoreValue" data-tone={tone}>
            {mean.toFixed(1)}
          </span>
          <span className="lq-ex__scoreTrack" aria-hidden="true">
            <span
              className="lq-ex__scoreFill"
              data-tone={tone}
              style={{ width: `${String(Math.max(0, Math.min(100, mean)))}%` }}
            />
          </span>
        </div>
        <div className="lq-ex__sub lq-nowrap">{modelLine(t, scores)}</div>
      </div>
    );
  }

  if (busy) return <span className="lq-ex__sub">{t('executions.score.computing')}</span>;

  if (execution.may_publish_a_score) {
    return (
      <div className="lq-ex__scoreBox">
        <button
          type="button"
          className="lq-ex__compute"
          title={t('executions.score.computeMeaning')}
          onClick={onCompute}
        >
          {t('executions.score.compute')}
        </button>
        {failed ? (
          <div className="lq-ex__sub lq-ex__sub--bad" role="alert">
            {t('executions.score.failed')}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <span className="lq-ex__cant">
      <span className="lq-ex__cantTrack" aria-hidden="true" />
      <span className="lq-ex__sub">
        {execution.state === 'running' || execution.state === 'pending'
          ? t('executions.score.afterItFinishes')
          : t('executions.score.notEnough')}
      </span>
    </span>
  );
}

/* When the run started, relative — "did the nightly sweep run" is what this column is for — and how
 * long it took, or how long it has been going. The exact instant is on hover. */
function WhenCell({
  execution,
  now,
}: {
  readonly execution: Execution;
  readonly now: Date;
}): ReactElement {
  const { t } = useTranslation();
  const { started_at: started, finished_at: finished } = execution;
  const minutesBetween = (from: string, to: Date): number | null => {
    const ms = to.getTime() - new Date(from).getTime();
    return Number.isNaN(ms) ? null : Math.max(0, Math.round(ms / 60000));
  };

  let duration: string;
  if (started === null) {
    duration = t('executions.notStarted');
  } else if (finished !== null) {
    const minutes = minutesBetween(started, new Date(finished));
    duration = minutes === null ? '—' : t('executions.minutes', { count: minutes });
  } else {
    const minutes = minutesBetween(started, now) ?? 0;
    duration =
      minutes < 120
        ? t('executions.runningMinutes', { count: minutes })
        : t('executions.runningHours', { count: Math.floor(minutes / 60) });
  }

  return (
    <div className="lq-ex__when">
      <div title={started ?? undefined}>
        {started === null
          ? t('executions.queuedShort')
          : (ageText(t, started, now) ?? started)}
      </div>
      <div className="lq-ex__sub">{duration}</div>
    </div>
  );
}

/* Who or what asked for the run. A person reads as their name; an unattended actor in muted text,
 * because "who did this" has a different follow-up for a timer than for a colleague. A `system:`
 * subject this build does not recognise is named as unattended rather than guessed at. */
function Requester({ requestedBy }: { readonly requestedBy: string }): ReactElement {
  const { t } = useTranslation();
  const origin = sourceOf(requestedBy);
  const system = requestedBy.startsWith('system:');
  const label =
    origin === 'scheduled' || origin === 'after_harvest'
      ? t(`scheduling.source.${origin}`)
      : system
        ? t('executions.who.unattended', { actor: requestedBy.slice('system:'.length) })
        : requestedBy.trim() === ''
          ? t('scheduling.source.unknown')
          : personName(requestedBy);
  return (
    <span
      className={
        system || origin === 'unknown' ? 'lq-ex__who lq-ex__who--system' : 'lq-ex__who'
      }
      title={requestedBy === '' ? t('scheduling.source.unknownMeaning') : requestedBy}
    >
      {label}
    </span>
  );
}

const TASK_CHIP = {
  pending: { tone: 'unknown', hollow: true },
  running: { tone: 'info', hollow: false },
  succeeded: { tone: 'ok', hollow: false },
  failed: { tone: 'error', hollow: false },
  cancelled: { tone: 'unknown', hollow: false },
} as const satisfies Record<
  Task['state'],
  { readonly tone: string; readonly hollow: boolean }
>;

/* Each task of an opened run: the rule and its columns, the table, its state, and how much of what
 * it read passed. Read only for the run that is open, so the ledger costs one request per run a
 * reader actually looks into. */
function TaskList({ executionId }: { readonly executionId: string }): ReactElement {
  const { t } = useTranslation();
  const tasks = useExecutionTasks(executionId);
  const ruleNames = useRuleNamesByVersion();

  if (tasks.isPending) return <SkeletonRows rows={2} columns={4} />;
  if (tasks.isError) {
    return <p className="lq-ex__sub">{t('executions.tasks.unreadable')}</p>;
  }
  if (tasks.data.items.length === 0) {
    return <p className="lq-ex__sub">{t('executions.tasks.none')}</p>;
  }

  return (
    <ul className="lq-ex__tasks" aria-label={t('executions.tasks.label')}>
      {tasks.data.items.map((task) => {
        const chip = TASK_CHIP[task.state];
        const name = ruleNames.get(task.rule_version_id);
        const measured = task.state === 'succeeded';
        return (
          <li key={task.id} className="lq-ex__task">
            <div className="lq-ex__taskRule">
              <div
                className={
                  name === undefined ? 'lq-ex__taskName lq-muted' : 'lq-ex__taskName'
                }
              >
                {name ?? t('executions.task.ruleUnlisted')}
              </div>
              {task.columns.length > 0 ? (
                <div className="lq-ex__taskCols">{task.columns.join(', ')}</div>
              ) : null}
            </div>
            <span className="lq-ex__taskDataset">
              <DatasetKindIcon datasetId={task.dataset_id} />
              {task.qualified_name}
            </span>
            <span className="lq-cn__state lq-ex__taskState" data-tone={chip.tone}>
              <span
                aria-hidden="true"
                className={chip.hollow ? 'lq-cn__dot lq-cn__dot--hollow' : 'lq-cn__dot'}
              />
              {t(`executions.state.${task.state}`)}
            </span>
            <div className="lq-ex__taskPass">
              {task.pass_rate === null ? null : (
                <span className="lq-ex__passTrack" aria-hidden="true">
                  <span
                    className="lq-ex__passFill"
                    style={{ width: `${String(task.pass_rate * 100)}%` }}
                  />
                </span>
              )}
              <span className="lq-ex__passText">
                {task.pass_rate === null
                  ? measured
                    ? t('executions.task.nothingExamined')
                    : '—'
                  : t('executions.task.passRate', {
                      percent: Math.round(task.pass_rate * 100),
                    })}
              </span>
              <span className="lq-ex__sub">
                {measured
                  ? t('executions.findings.violations', {
                      count: task.violation_count,
                      shown: compactNumber(task.violation_count),
                    })
                  : (task.detail ?? '')}
              </span>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/* Cancel, offered only on a run that never started.
 *
 * **Queued only, because that is the only cancellation that holds.** The run loop does not watch for
 * cancellation, so a *running* run marked cancelled would keep scanning the customer's tables under
 * a label saying it had stopped. A queued run has nothing working on it: once cancelled, a workflow
 * that later starts finds it finished and scans nothing. This is the control a run stranded in the
 * queue needs — before it existed, such a run blocked the same rules for six hours.
 *
 * Hidden rather than disabled without the permission: a reader sees every run and has no use for a
 * control they may not press. `can` denies while the identity read is in flight, so the button
 * appears once the answer is known rather than appearing and then vanishing. */
function CancelQueuedRun({
  execution,
}: {
  readonly execution: Execution;
}): ReactElement | null {
  const { t } = useTranslation();
  const identity = useIdentity();
  const cancel = useCancelExecution();
  const [asking, setAsking] = useState(false);

  if (execution.state !== 'pending' || !can(identity.data, CANCEL_EXECUTION_PERMISSION)) {
    return null;
  }
  const run = shortRunId(execution.id);
  return (
    <>
      <Button
        tone="secondary"
        size="compact"
        onClick={() => {
          setAsking(true);
        }}
      >
        {t('executions.cancel.action')}
      </Button>
      <ConfirmDialog
        open={asking}
        title={t('executions.cancel.title', { run })}
        message={t('executions.cancel.body')}
        confirmLabel={t('executions.cancel.confirm')}
        cancelLabel={t('executions.cancel.keep')}
        tone="danger"
        busy={cancel.isPending}
        error={cancel.isError ? cancel.error.message : undefined}
        onConfirm={() => {
          cancel.mutate(execution.id, {
            onSuccess: () => {
              setAsking(false);
            },
          });
        }}
        onCancel={() => {
          setAsking(false);
          cancel.reset();
        }}
      />
    </>
  );
}

/* What opens under a run: the tables it covered, its plan, whether it may publish a score and who
 * asked; the scores it produced; the run's own detail; and each task. */
function RunDetail({
  execution,
  scores,
}: {
  readonly execution: Execution;
  readonly scores: readonly Score[] | undefined;
}): ReactElement {
  const { t } = useTranslation();
  const names = [...new Set(execution.targets.map((target) => target.qualified_name))];
  const shown = names.slice(0, TARGETS_SHOWN);
  const origin = sourceOf(execution.requested_by);

  return (
    <div className="lq-ex__detail">
      <div className="lq-ex__facts">
        <span>
          {t('executions.expand.targets')}{' '}
          {names.length === 0 ? (
            t('executions.expand.noTargets')
          ) : (
            <span className="lq-mono">{shown.join(', ')}</span>
          )}
          {names.length > shown.length
            ? ` ${t('executions.expand.moreTargets', { count: names.length - shown.length })}`
            : null}
        </span>
        <span>
          {t('executions.expand.plan')}{' '}
          <span className="lq-mono" title={execution.plan_hash}>
            {execution.plan_hash.slice(0, 12)}
          </span>
        </span>
        <span
          className="lq-ex__permit"
          data-ok={execution.may_publish_a_score ? 'true' : undefined}
        >
          {execution.may_publish_a_score
            ? t('executions.score.may')
            : execution.state === 'running' || execution.state === 'pending'
              ? t('executions.score.after')
              : t('executions.score.cannot')}
        </span>
        <span>
          {t('executions.expand.requested', {
            source: t(`scheduling.source.${origin}`),
          })}
        </span>
        <Link className="lq-ex__open" to={`/executions/${execution.id}`}>
          {t('executions.open')}
        </Link>
        <CancelQueuedRun execution={execution} />
      </div>

      {scores !== undefined && scores.length > 0 ? (
        <div className="lq-ex__scores">
          <div className="lq-ex__scoresTitle">{t('executions.expand.scoreTitle')}</div>
          {scores.map((score) => (
            <div key={score.id} className="lq-ex__scoreRow">
              <span className="lq-mono">{score.qualified_name}</span>
              <strong>
                {score.value === null
                  ? t('executions.score.notComputable')
                  : score.value.toFixed(1)}
              </strong>
              <span className="lq-ex__sub">
                {t('executions.score.modelVersion', {
                  version: score.score_model_version,
                })}
              </span>
            </div>
          ))}
        </div>
      ) : null}

      {execution.detail === null ? null : (
        <div className="lq-ex__band">{execution.detail}</div>
      )}

      <TaskList executionId={execution.id} />
    </div>
  );
}

export function ExecutionsScreen(): ReactElement {
  const { t } = useTranslation();
  const executions = useExecutions();
  const findings = useExecutionFindings();
  const compute = useComputeScores();
  /* **Client-side, because the route takes no source parameter and inventing one would be a
     parallel backend.** The listing is one bounded page, so filtering it here is exact for
     what is on screen. */
  const [source, setSource] = useState<SourceFilter>('all');
  /* One run open at a time, as the design draws it: the opened run's tasks are a read of their own,
     and a reader comparing two runs opens the run pages. */
  const [openId, setOpenId] = useState<string | null>(null);
  /* The scores each run returned when Compute score was pressed. Kept per run for the page's life;
     the POST is idempotent, so pressing again after a reload returns the same stored figures. */
  const [computed, setComputed] = useState<ReadonlyMap<string, readonly Score[]>>(
    () => new Map(),
  );
  const [failedId, setFailedId] = useState<string | null>(null);

  /* Keyed by run, so a total cannot be paired with the wrong row by index. */
  const findingsById = new Map(
    (findings.data?.items ?? []).map((each) => [each.execution_id, each]),
  );

  /* One clock read per render, passed down, so the rows of one table share one "now". */
  const now = new Date();

  const all = executions.data?.items ?? [];
  const shown = all.filter((execution) => matchesSource(execution.requested_by, source));
  const running = all.filter((each) => each.state === 'running').length;
  const queued = all.filter((each) => each.state === 'pending').length;
  const attention = all.filter(
    (each) => each.state === 'partial' || each.state === 'failed',
  ).length;

  const computeFor = (executionId: string) => (event: MouseEvent) => {
    /* The button sits inside the row that opens on click; computing must not also open it. */
    event.stopPropagation();
    setFailedId(null);
    compute.mutate(executionId, {
      onSuccess: (page) => {
        setComputed((current) => new Map(current).set(executionId, page.items));
      },
      onError: () => {
        setFailedId(executionId);
      },
    });
  };

  return (
    <>
      <PageHeader
        title={t('executions.title')}
        lede={t('executions.lede')}
        help={t('help.feature.executions')}
      />

      <div className="lq-stack">
        {executions.isSuccess ? (
          /* The three counts the design leads with, from the page on screen — the listing is one
             bounded page, and these describe it rather than all history. */
          <div
            className="lq-ex__counts"
            role="group"
            aria-label={t('executions.counts.label')}
          >
            <span>
              <strong className="lq-ex__count lq-ex__count--running">{running}</strong>
              {t('executions.counts.running')}
            </span>
            <span>
              <strong className="lq-ex__count lq-ex__count--queued">{queued}</strong>
              {t('executions.counts.queued')}
            </span>
            <span>
              <strong className="lq-ex__count lq-ex__count--attention">
                {attention}
              </strong>
              {t('executions.counts.attention')}
            </span>
          </div>
        ) : null}

        {/* **Manual / Scheduled / After harvest.** "Did the nightly sweep run" and "what did somebody
            kick off by hand" are the two things a reader comes to this ledger to separate. */}
        <div className="lq-ex__filter">
          <SegmentedFilter
            variant="track"
            label={t('scheduling.source.column')}
            value={source}
            onChange={setSource}
            options={SOURCE_FILTERS.map((id) => ({
              id,
              label: t(`scheduling.source.${id}`),
            }))}
          />
          <HelpLabel
            subject={t('scheduling.source.column')}
            help={t('help.field.runSource')}
            inline
          >
            <span className="lq-ex__hint">{t('executions.filterHint')}</span>
          </HelpLabel>
        </div>

        {executions.isError && (
          <Alert tone="error" title={t('executions.unreachable')}>
            {executions.error.message}
          </Alert>
        )}

        {executions.isPending && (
          <>
            <p role="status" className="lq-muted">
              {t('executions.loading')}
            </p>
            <SkeletonRows rows={4} columns={5} />
          </>
        )}

        {executions.isSuccess && all.length === 0 ? (
          <EmptyState
            title={t('executions.empty.title')}
            guidance={t('executions.empty.guidance')}
          />
        ) : null}

        {executions.isSuccess && all.length > 0 ? (
          <div className="lq-ex__card">
            <div className="lq-ex__scroll">
              <div
                role="table"
                aria-label={t('executions.tableCaption')}
                className="lq-ex__table"
              >
                <div role="rowgroup">
                  <div role="row" className="lq-ex__grid lq-ex__head">
                    <span role="columnheader" aria-label={t('executions.column.open')} />
                    <span role="columnheader">
                      <HelpLabel
                        subject={t('executions.column.state')}
                        help={t('help.field.executionResult')}
                        inline
                      >
                        <span>{t('executions.column.state')}</span>
                      </HelpLabel>
                    </span>
                    <span role="columnheader">{t('executions.column.run')}</span>
                    <span role="columnheader">
                      <HelpLabel
                        subject={t('executions.column.progress')}
                        help={t('help.field.executionTasks')}
                        inline
                      >
                        <span>{t('executions.column.progress')}</span>
                      </HelpLabel>
                    </span>
                    <span role="columnheader">{t('executions.column.findings')}</span>
                    <span role="columnheader">{t('executions.column.score')}</span>
                    <span role="columnheader" className="lq-ex__right">
                      {t('executions.column.when')}
                    </span>
                  </div>
                </div>
                <div role="rowgroup">
                  {shown.length === 0 ? (
                    <div role="row" className="lq-ex__none">
                      <span role="cell">{t('executions.noMatch')}</span>
                    </div>
                  ) : null}
                  {shown.map((execution) => {
                    const open = openId === execution.id;
                    const toggle = (): void => {
                      setOpenId(open ? null : execution.id);
                    };
                    return (
                      <div
                        key={execution.id}
                        className="lq-ex__item"
                        data-open={open ? 'true' : undefined}
                        data-live={execution.state === 'running' ? 'true' : undefined}
                      >
                        {/* The whole row opens it, as the design draws; the chevron is the
                            keyboard's way in and carries the state for assistive technology. */}
                        <div
                          role="row"
                          className="lq-ex__grid lq-ex__row"
                          onClick={toggle}
                        >
                          <span role="cell">
                            <button
                              type="button"
                              className="lq-ex__chevron"
                              aria-expanded={open}
                              aria-label={t(
                                open ? 'executions.row.hide' : 'executions.row.show',
                                { run: shortRunId(execution.id) },
                              )}
                              onClick={(event) => {
                                event.stopPropagation();
                                toggle();
                              }}
                            >
                              ▶
                            </button>
                          </span>
                          <span role="cell">
                            <StateChip state={execution.state} />
                          </span>
                          <div role="cell" className="lq-ex__runCell">
                            <div className="lq-ex__run">{shortRunId(execution.id)}</div>
                            <div className="lq-ex__sub">
                              <Requester requestedBy={execution.requested_by} />
                              {` · ${execution.strategy} · ${execution.dialect}`}
                            </div>
                          </div>
                          <div role="cell">
                            <ProgressCell execution={execution} />
                          </div>
                          <div role="cell">
                            <FindingsCell
                              execution={execution}
                              findings={findingsById.get(execution.id)}
                            />
                          </div>
                          <div role="cell" className="lq-ex__scoreCell">
                            <ScoreCell
                              execution={execution}
                              scores={computed.get(execution.id)}
                              busy={
                                compute.isPending && compute.variables === execution.id
                              }
                              failed={failedId === execution.id}
                              onCompute={computeFor(execution.id)}
                            />
                          </div>
                          <div role="cell" className="lq-ex__right">
                            <WhenCell execution={execution} now={now} />
                          </div>
                        </div>
                        {open ? (
                          <div role="row" className="lq-ex__openRow">
                            <div role="cell" aria-colspan={7}>
                              <RunDetail
                                execution={execution}
                                scores={computed.get(execution.id)}
                              />
                            </div>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}
